import React, { createContext, useContext, useEffect, useState } from "react";
import { api, setNativeToken, setNativeRefresh, getNativeRefresh } from "@/lib/api";
import { isNative, saveTokens, loadTokens, clearTokens, initNative, biometricLockEnabled, biometricUnlock } from "@/lib/native";
import { saveSnapshot, db } from "@/lib/db";
import { startSyncListener, flushQueue } from "@/lib/syncQueue";
import { requestPersistentStorage, purgeIfQuotaHigh } from "@/lib/pwa";

const AuthContext = createContext(null);

// ---- Migration transitoire : purge du legacy crm_token au tout premier montage
// L'authentification est désormais entièrement portée par le cookie httpOnly.
// L'indicateur non sensible `bevid_session` reste dans localStorage pour piloter
// le démarrage hors-ligne.
try { localStorage.removeItem("crm_token"); } catch { /* noop */ }

async function bootstrapOffline() {
  try {
    const { data } = await api.get("/sync/bootstrap");
    await saveSnapshot(data);
  } catch { /* silencieux — cache probable */ }
}

async function getSnapshotAt() {
  try { const m = await db.meta.get("snapshot_at"); return m?.value || null; }
  catch { return null; }
}

async function loadCachedUser() {
  try { const m = await db.meta.get("cached_user"); return m?.value || null; }
  catch { return null; }
}

async function cacheUser(u) {
  try { await db.meta.put({ key: "cached_user", value: u }); } catch { /* noop */ }
}

const SESSION_FLAG = "bevid_session";
function markSession()    { try { localStorage.setItem(SESSION_FLAG, "1"); } catch { /* best-effort : ignoré volontairement */ } }
function clearSession()   { try { localStorage.removeItem(SESSION_FLAG); } catch { /* best-effort : ignoré volontairement */ } }
function hasSessionFlag() { try { return !!localStorage.getItem(SESSION_FLAG); } catch { return false; } }

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offlineMode, setOfflineMode] = useState(false);
  const [snapshotAt, setSnapshotAt] = useState(null);

  useEffect(() => {
    const stop = startSyncListener();
    const onOffline = () => setOfflineMode(true);
    const onRevoked = () => { setUser(null); clearSession(); };
    window.addEventListener("bevid:session-revoked", onRevoked);
    const onOnline = () => {
      setOfflineMode(false);
      if (hasSessionFlag()) {
        api.get("/auth/me").then(async (r) => {
          setUser(r.data);
          await cacheUser(r.data);
          markSession();
          bootstrapOffline();
        }).catch(() => {});
      }
    };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);

    if (!hasSessionFlag()) {
      setLoading(false);
      return () => { stop(); window.removeEventListener("offline", onOffline); window.removeEventListener("online", onOnline); };
    }

    // Natif (N3) : recharge le jeton porteur avant /auth/me (le cookie HttpOnly n'existe pas dans le WebView)
    // Natif (N2/N3) : verrou biométrique optionnel, puis jetons rechargés depuis Keychain/Keystore avant /auth/me
    (isNative()
      ? initNative().then(async () => { if (await biometricLockEnabled()) await biometricUnlock(); const t = await loadTokens(); setNativeToken(t.token); setNativeRefresh(t.refresh_token); })
      : Promise.resolve())
      .then(() => api.get("/auth/me"))
       .then(async (r) => {
         setUser(r.data);
         await cacheUser(r.data);
         markSession();
         setOfflineMode(false);
         bootstrapOffline();
         flushQueue().catch(() => {});
       })
       .catch(async (err) => {
         const status = err?.response?.status;
         if (status === 401 || status === 403) {
           clearSession();
           await db.meta.delete("cached_user").catch(() => {});
           setUser(null);
         } else {
           // Panne réseau → mode hors-ligne à partir du cache
           const cached = await loadCachedUser();
           const snap = await getSnapshotAt();
           if (cached) {
             setUser(cached);
             setOfflineMode(true);
             setSnapshotAt(snap);
           }
         }
       })
       .finally(() => setLoading(false));

    return () => { stop(); window.removeEventListener("offline", onOffline); window.removeEventListener("online", onOnline); };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (isNative() && data.token) { setNativeToken(data.token); setNativeRefresh(data.refresh_token); await saveTokens(data); }
    markSession();
    setUser(data.user);
    await cacheUser(data.user);
    setOfflineMode(false);
    bootstrapOffline();
    // Lot Fiabilité PWA — demande stockage persistant après connexion (non bloquant).
    // Sur iOS le navigateur exigera aussi la permission notification ; l'UI /pwa-status
    // guide l'enchaînement quand persist() renvoie false.
    requestPersistentStorage().then(() => purgeIfQuotaHigh()).catch(() => {});
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (isNative() && data.token) { setNativeToken(data.token); setNativeRefresh(data.refresh_token); await saveTokens(data); }
    markSession();
    setUser(data.user);
    await cacheUser(data.user);
    setOfflineMode(false);
    bootstrapOffline();
    requestPersistentStorage().then(() => purgeIfQuotaHigh()).catch(() => {});
    return data.user;
  };

  const logout = async () => {
    // Désabonnement push propre : l'ancien appareil ne doit plus recevoir les
    // notifications de ce compte.
    try {
      const { unsubscribePush } = await import("@/lib/push");
      await unsubscribePush();
    } catch { /* silencieux */ }
    try { await api.post("/auth/logout", getNativeRefresh() ? { refresh_token: getNativeRefresh() } : {}); } catch { /* silencieux */ }
    setNativeToken(null); setNativeRefresh(null); clearTokens().catch(() => {});
    clearSession();
    db.meta.delete("cached_user").catch(() => {});
    setUser(null);
    window.location.href = "/login";
  };

  return <AuthContext.Provider value={{ user, loading, login, register, logout, offlineMode, snapshotAt }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
